import * as React from 'react';
import { FC } from 'react';
import moment from 'moment';
import { Day, Match, MatchResult, TimeZone } from './types';
import locales from './locales';
import ballImage from './euro_ball.png';

const Completed = () => <div className="t-matches_completed">{locales().completed}</div>;

const MakeBetText: FC<{ game: string | null; sameDate: boolean }> = ({ game, sameDate }) => {
  const l = locales();

  if (!game) return null;

  return (
    <div className="t-matches_text">
      {l.make_bet_1}&nbsp;
      <span className="t-matches_text_red">{l.make_bet_2}</span>&nbsp;
      {sameDate ? `${l.make_bet_3}` : `${l.make_bet_3_not}`}&nbsp;
      <span className="t-matches_text_red">{l.make_bet_4}</span>&nbsp;
      {l.make_bet_5}&nbsp;
      {game}!
      <div className="t-matches_euro-ball">
        <img src={ballImage} alt="euro 2020" />
      </div>
    </div>
  );
};

const Result: FC<{ result: MatchResult }> = ({ result }) => {
  const l = locales();

  switch (result) {
    case 'success':
      return (
        <div className="m-result">
          <i className="rs-icon-check-circle m-result_icon m-result_icon__success" />
          <span className="m-result_text">{l.success}</span>
        </div>
      );
    case 'fail':
      return (
        <div className="m-result">
          <i className="rs-icon-exclamation-triangle m-result_icon m-result_icon__fail" />
          <span className="m-result_text">{l.fail}</span>
        </div>
      );
    default:
      return (
        <div className="m-result m-result__default">
          <i className="rs-icon-times m-result_icon" />
          <span className="m-result_text">{l.none}</span>
        </div>
      );
  }
};

const matchTime = (startsAt: string, timeZone: TimeZone) =>
  moment(startsAt)
    .utcOffset(timeZone === 'CET' ? 120 : 180, false)
    .format('HH:mm');

const LinkButton: FC<{ active: boolean; url: string }> = ({ active, url }) => {
  const l = locales();

  if (!active || !url) {
    return <span className="rs-button m-item_button disabled">{l.place_bet}</span>;
  }

  return (
    <a href={url} className="rs-button m-item_button">
      {l.place_bet}
    </a>
  );
};

const MatchItem: FC<{ match: Match; timeZone: TimeZone; today: boolean }> = ({ match, timeZone, today }) => {
  const matchDate = moment.utc(match.starts_at || Date.now());
  const betable = today && matchDate.isAfter(Date.now());
  const resultable = today || matchDate.isBefore(Date.now());

  return (
    <div className="match_item m-item" role="listitem">
      <div className="m-item_left">
        {match.name && <i className="rs-icon-football m-item_ball" />}
        <div className="m-item_name">{match.name}</div>
      </div>
      <div className="m-item_middle">
        <div className="m-item_date">
          <i className="rs-icon-clock m-item_clock" />
          <span>{match.starts_at ? `${matchTime(match.starts_at, timeZone)} ${timeZone}` : ''}</span>
        </div>
        {resultable && <Result result={match.result} />}
      </div>
      <div className="m-item_buttons">
        <LinkButton active={betable} url={match.bet_url || ''} />
      </div>
    </div>
  );
};

const DayMatches: FC<{ day: Day; timeZone: TimeZone }> = ({ day, timeZone }) => {
  const dayDate = moment.utc(day.date);
  const currentDate = moment.utc(Date.now()).startOf('day');
  const sameDate = dayDate.isSame(currentDate);

  return (
    <div className="tournament_matches t-matches">
      <div className="t-matches_top">
        <div className="t-matches_date">
          <div className="t-matches_date__day">{dayDate.format('DD')}</div>
          <div className="t-matches_date__month">{locales()[dayDate.month() === 5 ? 'june' : 'july']}</div>
        </div>
        <MakeBetText game={day.game} sameDate={sameDate} />
      </div>
      <div className="t-matches_list" role="list">
        {day.matches.map((m) => (
          <MatchItem key={m.name} match={m} timeZone={timeZone} today={sameDate} />
        ))}
      </div>
    </div>
  );
};

const Matches: FC<{ day: Day | undefined; timeZone: TimeZone }> = ({ day, timeZone }) =>
  day ? <DayMatches day={day} timeZone={timeZone} /> : <Completed />;

export default Matches;
